import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { copyImageFromSourceChain } from '../../util/imageClipboard';
import { getAttachmentSources, downloadReadOnlyAttachment } from '../../util/readOnlyAttachmentActions';
import useCoordinatedDropdown from '../../hooks/useCoordinatedDropdown';
import { standardMenuSurface, standardMenuItem } from '../../appearance/menuStyles';
import { FiCopy, FiFileText, FiMoreVertical, FiDownload } from 'react-icons/fi';

const copyText = async text => {
    const value = String(text || '');
    if (!value.trim()) {
        return false;
    }

    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
};

const ReadOnlyMessageActions = ({
    message,
    onSaveMessage = null,
    saving = false,
    onStatus = null,
    placement = 'overlay'
}) => {
    const {t: lnT} = useTranslation();

    const [open, setOpen] = useCoordinatedDropdown();
    const [attachmentBusy, setAttachmentBusy] = useState(false);
    const [attachmentStatus, setAttachmentStatus] = useState('');
    const [menuPosition, setMenuPosition] = useState(null);
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const hasTitle = Boolean(String(message?.title || '').trim());
    const hasBody = Boolean(String(message?.body || '').trim());
    const hasImage = getAttachmentSources(message?.image, true).length > 0;
    const hasFile = getAttachmentSources(message?.file).length > 0;
    const canCopyImage = hasImage && Boolean(navigator.clipboard?.write) && typeof ClipboardItem !== 'undefined';
    const canSaveMessage = typeof onSaveMessage === 'function';

    const updateMenuPosition = () => {
        if (!buttonRef.current || typeof window === 'undefined') {
            return;
        }

        const rect = buttonRef.current.getBoundingClientRect();
        const menuWidth = 180;
        const menuGap = 6;
        const viewportPadding = 8;
        const left = Math.min(
            Math.max(rect.right - menuWidth, viewportPadding),
            window.innerWidth - menuWidth - viewportPadding
        );
        const menuHeight = menuRef.current?.offsetHeight ||
            [canSaveMessage, hasImage, canCopyImage, hasFile, hasTitle, hasBody].filter(Boolean).length * 40 + 2;
        const top = Math.max(viewportPadding, Math.min(
            rect.bottom + menuGap,
            window.innerHeight - menuHeight - viewportPadding
        ));

        setMenuPosition({ left, top });
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const closeOnOutside = event => {
            if (
                wrapRef.current &&
                !wrapRef.current.contains(event.target) &&
                !menuRef.current?.contains(event.target)
            ) {
                setOpen(false);
            }
        };
        const closeOnEscape = event => {
            if (event.key === 'Escape') {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };

        document.addEventListener('click', closeOnOutside);
        document.addEventListener('keydown', closeOnEscape, true);
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () => {
            document.removeEventListener('click', closeOnOutside);
            document.removeEventListener('keydown', closeOnEscape, true);
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [open, setOpen]);

    useLayoutEffect(() => {
        if (open) {
            updateMenuPosition();
        }
    }, [open]);

    if (!canSaveMessage && !hasTitle && !hasBody && !hasImage && !hasFile) {
        return null;
    }

    const handleCopy = async (label, value) => {
        setOpen(false);
        const didCopy = await copyText(value);
        if (typeof onStatus === 'function') {
            onStatus(didCopy ? `${label} copied.` : `No ${label.toLowerCase()} to copy.`);
        }
    };

    const handleAttachment = async (attachment, image, copy = false) => {
        if (attachmentBusy) return;
        setAttachmentBusy(true);
        setAttachmentStatus('');
        setOpen(false);
        let status;
        try {
            const completed = copy
                ? await copyImageFromSourceChain(getAttachmentSources(attachment, true))
                : await downloadReadOnlyAttachment(attachment, image);
            if (!completed) {
                if (copy) throw new Error('Copy unavailable');
                return; // Native save dialog was cancelled.
            }
            status = copy ? lnT('messages:copied') : lnT('messages:downloadStarted', { value1: attachment.displayName || attachment.name || lnT('messages:downloadImage') });
        } catch (_) {
            status = copy ? lnT('messages:copyFailed') : lnT('messages:actionFailed', { value1: image ? lnT('messages:downloadImage_3ac575dc') : lnT('messages:downloadFile') });
        } finally { setAttachmentBusy(false); }
        setAttachmentStatus(status);
        onStatus?.(status);
    };

    return (
        <ActionsWrap ref={wrapRef} $placement={placement} onClick={event => event.stopPropagation()}>
            <MoreButton
                ref={buttonRef}
                type="button"
                aria-label={lnT('messages:moreMessageActions')}
                title={lnT('folders:moreActions')}
                disabled={attachmentBusy}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(current => !current);
                }}
            >
                <FiMoreVertical size={16} />
            </MoreButton>
            {open && menuPosition && typeof document !== 'undefined' ? createPortal(
                <Menu
                    ref={menuRef}
                    role="menu"
                    $top={menuPosition.top}
                    $left={menuPosition.left}
                    onClick={event => event.stopPropagation()}
                >
                    {canSaveMessage ? (
                        <MenuItem role="menuitem"
                            type="button"
                            disabled={saving}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setOpen(false);
                                onSaveMessage(message);
                            }}
                        >
                            <FiCopy size={14} />
                            {saving ? lnT('messages:savingCopy') : lnT('messages:saveToMyInbox')}
                        </MenuItem>
                    ) : null}
                    {hasImage ? <MenuItem role="menuitem" type="button" disabled={attachmentBusy} onClick={() => handleAttachment(message.image, true)}>
                        <FiDownload size={14} />{lnT('messages:downloadImage_3ac575dc')}</MenuItem> : null}
                    {canCopyImage ? <MenuItem role="menuitem" type="button" disabled={attachmentBusy} onClick={() => handleAttachment(message.image, true, true)}>
                        <FiCopy size={14} />{lnT('messages:copyImage')}</MenuItem> : null}
                    {hasFile ? <MenuItem role="menuitem" type="button" disabled={attachmentBusy} onClick={() => handleAttachment(message.file, false)}>
                        <FiDownload size={14} />{lnT('messages:downloadFile')}</MenuItem> : null}
                    {hasTitle ? (
                        <MenuItem role="menuitem" type="button" onClick={() => handleCopy('Title', message.title)}>
                            <FiCopy size={14} />{lnT('messages:copyTitle')}</MenuItem>
                    ) : null}
                    {hasBody ? (
                        <MenuItem role="menuitem" type="button" onClick={() => handleCopy('Note', message.body)}>
                            <FiFileText size={14} />{lnT('messages:copyNote')}</MenuItem>
                    ) : null}
                </Menu>,
                document.body
            ) : null}
            {attachmentStatus && !onStatus ? <AttachmentStatus role="status">{attachmentStatus}</AttachmentStatus> : null}
        </ActionsWrap>
    );
};

const ActionsWrap = styled.div`
    position: ${({ $placement }) => ($placement === 'inline' ? 'relative' : 'absolute')};
    top: ${({ $placement }) => ($placement === 'inline' ? '0' : '8px')};
    right: ${({ $placement }) => ($placement === 'outside-card' ? '0' : $placement === 'inline' ? 'auto' : '8px')};
    flex: 0 0 auto;
    margin-left: ${({ $placement }) => ($placement === 'inline' ? '8px' : '0')};
    z-index: 3;
`;

const MoreButton = styled.button`
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.86);
    background: rgba(12, 14, 16, 0.72);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    cursor: pointer;

    &:hover {
        background: rgba(35, 39, 42, 0.92);
    }
`;

const Menu = styled.div`
    ${standardMenuSurface};
    position: fixed;
    top: ${({ $top }) => `${$top}px`};
    left: ${({ $left }) => `${$left}px`};
    width: 180px;
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    z-index: 10000;
`;

const MenuItem = styled.button`${standardMenuItem}; font-size: 13px;`;

export default ReadOnlyMessageActions;

const AttachmentStatus = styled.div`position: absolute; right: 0; top: 100%; width: 200px; padding: 8px; font-size: 12px; ${standardMenuSurface};`;
